import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getUser } from '../auth'
import { api } from '../api'
import { useToast } from '../components/Toast'
import { useAsyncData } from '../hooks/useAsyncData'
import { useColumnPrefs } from '../hooks/useColumnPrefs'
import RefreshButton from '../components/RefreshButton'
import HScroll from '../components/HScroll'
import Spinner, { PageSpinner } from '../components/Spinner'
import CapabilityPicker from '../components/CapabilityPicker'
import ConnectionBadge from '../components/ConnectionBadge'
import ColumnsMenu from '../components/ColumnsMenu'
import ConfirmDialog from '../components/ConfirmDialog'
import {
  ENVIRONMENTS,
  CATEGORY_OPTIONS,
  LOCATION_OPTIONS,
  locationLabel,
  CATEGORY_COLORS,
  ENV_COLORS,
  modelsForCategory,
} from '../constants'

const EMPTY_FORM = {
  mdi: '',
  name: '',
  category: '',
  model: '',
  environment: '',
  location: 'UNKNOWN',
  is_virtual: false,
  notes: '',
}

// Optional columns the user can toggle. MDI and the Reserve action always show.
// Name and Connection lead, right after MDI: they're what you scan for first.
const TOGGLEABLE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'connection', label: 'Connection' },
  { key: 'category', label: 'Category' },
  { key: 'model', label: 'Model' },
  { key: 'environment', label: 'Environment' },
  { key: 'cloud_model', label: 'Cloud model' },
  { key: 'location', label: 'Location' },
  { key: 'is_virtual', label: 'Virtual' },
  { key: 'notes', label: 'Notes' },
  { key: 'reserved_by', label: 'Reserved by' },
]

// Cloud model + Notes default off to keep the table from overflowing.
const DEFAULT_COLUMNS = {
  name: true,
  connection: true,
  category: true,
  model: true,
  environment: true,
  cloud_model: false,
  location: true,
  is_virtual: false,
  notes: false,
  reserved_by: true,
}

export default function Devices() {
  const [currentUserId] = useState(() => getUser()?.id ?? null)
  const [reserving, setReserving] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [removeTarget, setRemoveTarget] = useState(null)
  // is_virtual is '' (any) | 'true' (virtual only) | 'false' (physical only); the
  // string form goes straight through buildQuery to the API's Optional[bool].
  // connection is '' (any) | 'online' | 'offline' | 'unknown' (Emporia Cloud).
  const [filters, setFilters] = useState({ environment: '', category: '', is_virtual: '', connection: '', capabilities: [] })
  const { columns, toggle: toggleColumn, reset: resetColumns, isDefault: isDefaultColumns } =
    useColumnPrefs('devices.visibleColumns', DEFAULT_COLUMNS)
  const [capCatalog, setCapCatalog] = useState([])
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState(EMPTY_FORM)
  const [adding, setAdding] = useState(false)
  const addDialogRef = useRef(null)
  const { show } = useToast()

  const { data, setData, loading, hasLoaded, reload } = useAsyncData(async () => {
    const params = {}
    if (filters.environment) params.environment = filters.environment
    if (filters.category) params.category = filters.category
    if (filters.is_virtual) params.is_virtual = filters.is_virtual
    if (filters.connection) params.connection = filters.connection
    if (filters.capabilities.length) params.capabilities = filters.capabilities
    const [devices, reservations] = await Promise.all([
      api.getDevices(params),
      api.getReservations({ includeAll: true }),
    ])
    return { devices, reservations }
  }, [filters], { onError: err => show(err.message, 'error') })

  const devices = data?.devices ?? []
  const reservations = data?.reservations ?? []

  useEffect(() => {
    api.getCapabilities().then(setCapCatalog).catch(() => { /* filter just stays empty */ })
  }, [])

  // Native <dialog> drives the add-device modal: focus trap, initial focus and
  // Escape all come from showModal().
  useEffect(() => {
    const el = addDialogRef.current
    if (!el) return
    if (addOpen && !el.open) el.showModal()
    else if (!addOpen && el.open) el.close()
  }, [addOpen])

  function closeAddModal() {
    setAddOpen(false)
    setAddForm(EMPTY_FORM)
  }

  async function reserve(device) {
    setReserving(device.id)
    try {
      await api.createReservation(device.id, 'DEVICE')
      show(`Reserved "${device.mdi}"`, 'success')
      const res = await api.getReservations({ includeAll: true })
      setData(d => ({ ...d, reservations: res }))
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setReserving(null)
    }
  }

  async function removeDevice(device) {
    setRemoving(device.id)
    try {
      await api.deleteDevice(device.id)
      show(`Removed "${device.mdi}"`, 'success')
      setData(d => ({ ...d, devices: d.devices.filter(x => x.id !== device.id) }))
      setRemoveTarget(null)
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setRemoving(null)
    }
  }

  function setFilter(key, value) {
    setFilters(f => ({ ...f, [key]: value }))
  }

  function setFormField(key, value) {
    setAddForm(f => {
      const next = { ...f, [key]: value }
      // Models depend on the category — reset a model that's no longer valid.
      if (key === 'category' && !modelsForCategory(value).includes(f.model)) next.model = ''
      return next
    })
  }

  async function addDevice(e) {
    e.preventDefault()
    setAdding(true)
    try {
      const body = {
        mdi: addForm.mdi.trim(),
        // Inventory's own label; blank means no name (the cloud's name is not used).
        name: addForm.name.trim() || null,
        category: addForm.category,
        model: addForm.model,
        environment: addForm.environment,
        location: addForm.location,
        is_virtual: addForm.is_virtual,
        notes: addForm.notes.trim() || null,
      }
      await api.createDevice(body)
      show(`Added "${body.mdi}"`, 'success')
      closeAddModal()
      reload()
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setAdding(false)
    }
  }

  // Map device ID → reservation for active reservations
  const activeReservations = new Map(
    reservations
      .filter(r => r.reservable_type === 'DEVICE')
      .map(r => [r.reservable_id, r])
  )

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <h1 className="text-2xl font-bold mr-1">Devices</h1>
        <button className="btn btn-sm btn-primary" onClick={() => setAddOpen(true)}>+ Add device</button>

        <select
          className="select select-sm select-bordered"
          aria-label="Filter by environment"
          value={filters.environment}
          onChange={e => setFilter('environment', e.target.value)}
        >
          {['', ...ENVIRONMENTS].map(v => (
            <option key={v} value={v}>{v || 'All environments'}</option>
          ))}
        </select>

        <select
          className="select select-sm select-bordered"
          aria-label="Filter by category"
          value={filters.category}
          onChange={e => setFilter('category', e.target.value)}
        >
          {['', ...CATEGORY_OPTIONS].map(v => (
            <option key={v} value={v}>{v || 'All categories'}</option>
          ))}
        </select>

        <select
          className="select select-sm select-bordered"
          aria-label="Filter by virtual or physical"
          value={filters.is_virtual}
          onChange={e => setFilter('is_virtual', e.target.value)}
        >
          <option value="">Virtual + physical</option>
          <option value="true">Virtual only</option>
          <option value="false">Physical only</option>
        </select>

        <select
          className="select select-sm select-bordered"
          aria-label="Filter by connection"
          title="Connection to Emporia Cloud, from the last sync (every 5 minutes)"
          value={filters.connection}
          onChange={e => setFilter('connection', e.target.value)}
        >
          <option value="">Any connection</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="unknown">Unknown</option>
        </select>

        {/* Advanced: filter by capability (device must have ALL selected) */}
        <CapabilityPicker
          selected={filters.capabilities}
          catalog={capCatalog}
          onChange={next => setFilters(f => ({ ...f, capabilities: next }))}
          size="sm"
          header="Must have all selected"
          onClear={() => setFilters(f => ({ ...f, capabilities: [] }))}
        />

        <ColumnsMenu
          options={TOGGLEABLE_COLUMNS}
          columns={columns}
          onToggle={toggleColumn}
          onReset={resetColumns}
          isDefault={isDefaultColumns}
        />

        <RefreshButton onClick={reload} />
      </div>

      {!hasLoaded ? (
        <PageSpinner />
      ) : devices.length === 0 ? (
        <div className="text-center py-20 opacity-40">No devices found</div>
      ) : (
        // Keep the current rows on screen while a filter change refetches — dimmed
        // rather than replaced by a spinner, so the table doesn't collapse and the
        // page doesn't jump. Rows stay clickable; actions are keyed by id, so acting
        // on a row that is about to be filtered out is still correct.
        <div
          aria-busy={loading}
          className={`transition-opacity ${loading ? 'opacity-50' : ''}`}
        >
          <HScroll className="rounded-box border border-base-300">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>MDI</th>
                  {columns.name && <th>Name</th>}
                  {columns.connection && <th>Connection</th>}
                  {columns.category && <th>Category</th>}
                  {columns.model && <th>Model</th>}
                  {columns.environment && <th title="Environment">Env</th>}
                  {columns.cloud_model && <th>Cloud model</th>}
                  {columns.location && <th>Location</th>}
                  {columns.is_virtual && <th>Virtual</th>}
                  {columns.notes && <th>Notes</th>}
                  {columns.reserved_by && <th title="Reserved by">Reserved</th>}
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {devices.map(device => {
                  const reservation = activeReservations.get(device.id)
                  const isReserved = !!reservation
                  const isOwnReservation = reservation?.reserved_by === currentUserId
                  return (
                    <tr key={device.id} className="hover">
                      <td className="font-mono text-sm font-medium whitespace-nowrap">
                        <Link to={`/devices/${device.id}`} className="link link-hover text-primary">
                          {device.mdi}
                        </Link>
                      </td>
                      {columns.name && (
                        <td className="max-w-[180px] truncate" title={device.name ?? undefined}>
                          {device.name ?? <span className="opacity-30">—</span>}
                        </td>
                      )}
                      {columns.connection && <td><ConnectionBadge device={device} /></td>}
                      {columns.category && (
                        <td>
                          <span className={`badge badge-sm ${CATEGORY_COLORS[device.category] ?? 'badge-neutral'}`}>
                            {device.category}
                          </span>
                        </td>
                      )}
                      {columns.model && <td className="opacity-70 whitespace-nowrap">{device.model ?? '—'}</td>}
                      {columns.environment && (
                        <td>
                          <span className={`badge badge-sm ${ENV_COLORS[device.environment] ?? 'badge-neutral'}`}>
                            {device.environment}
                          </span>
                        </td>
                      )}
                      {columns.cloud_model && <td className="opacity-70 whitespace-nowrap font-mono text-xs">{device.cloud_model ?? '—'}</td>}
                      {columns.location && (
                        <td className="opacity-70 whitespace-nowrap" title={device.location ?? undefined}>
                          {device.location ? locationLabel(device.location) : '—'}
                        </td>
                      )}
                      {columns.is_virtual && (
                        <td>{device.is_virtual ? <span className="badge badge-sm badge-ghost">virtual</span> : null}</td>
                      )}
                      {columns.notes && <td className="opacity-70 max-w-[200px] truncate">{device.notes ?? '—'}</td>}
                      {columns.reserved_by && (
                        <td className="whitespace-nowrap">
                          {isReserved && (
                            isOwnReservation
                              ? <span className="badge badge-sm badge-ghost">you</span>
                              : <span>{reservation.reserved_by_email ?? reservation.reserved_by.slice(0, 8) + '…'}</span>
                          )}
                        </td>
                      )}
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-sm btn-primary whitespace-nowrap"
                            disabled={reserving === device.id || isReserved}
                            aria-busy={reserving === device.id}
                            onClick={() => reserve(device)}
                          >
                            {reserving === device.id ? <Spinner size="xs" /> : 'Reserve'}
                          </button>
                          {/* Disabled buttons don't show their title, so the tooltip
                              lives on a wrapper and the reason is repeated sr-only. */}
                          <span
                            className={isReserved ? 'cursor-not-allowed' : undefined}
                            title={isReserved ? 'Reserved or bundled devices cannot be removed' : undefined}
                          >
                            <button
                              className={`btn btn-sm btn-ghost whitespace-nowrap ${isReserved ? 'btn-disabled' : 'text-error'}`}
                              disabled={removing === device.id || isReserved}
                              aria-busy={removing === device.id}
                              title={isReserved ? undefined : 'Remove device'}
                              onClick={() => setRemoveTarget(device)}
                            >
                              {removing === device.id ? <Spinner size="xs" /> : 'Remove'}
                              {isReserved && <span className="sr-only"> (reserved or bundled devices cannot be removed)</span>}
                            </button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </HScroll>
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove device"
        message={`Remove device "${removeTarget?.mdi}" from inventory? This cannot be undone.`}
        confirmLabel="Remove"
        busy={!!removeTarget && removing === removeTarget.id}
        onConfirm={() => removeDevice(removeTarget)}
        onClose={() => setRemoveTarget(null)}
      />

      <dialog
        ref={addDialogRef}
        className="modal"
        aria-labelledby="add-device-title"
        onCancel={e => { if (adding) e.preventDefault() }}
        onClose={() => { if (addOpen) closeAddModal() }}
      >
        <div className="modal-box w-full max-w-lg">
          <h3 id="add-device-title" className="font-bold text-lg mb-4">Add device</h3>
          <form onSubmit={addDevice} className="flex flex-col gap-3">
            <label className="form-control">
              <div className="label"><span className="label-text">MDI <span className="text-error">*</span></span></div>
              <input
                className="input input-bordered input-sm font-mono"
                placeholder="e.g. 000-0000000001"
                value={addForm.mdi}
                onChange={e => setFormField('mdi', e.target.value)}
                required
              />
            </label>

            <label className="form-control">
              <div className="label"><span className="label-text">Name</span></div>
              <input
                className="input input-bordered input-sm"
                placeholder="Optional, e.g. Cart charger 3"
                value={addForm.name}
                onChange={e => setFormField('name', e.target.value)}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="form-control">
                <div className="label"><span className="label-text">Category <span className="text-error">*</span></span></div>
                <select
                  className="select select-bordered select-sm"
                  value={addForm.category}
                  onChange={e => setFormField('category', e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {CATEGORY_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>

              <label className="form-control">
                <div className="label"><span className="label-text">Model <span className="text-error">*</span></span></div>
                <select
                  className="select select-bordered select-sm"
                  value={addForm.model}
                  onChange={e => setFormField('model', e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {modelsForCategory(addForm.category).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="form-control">
                <div className="label"><span className="label-text">Environment <span className="text-error">*</span></span></div>
                <select
                  className="select select-bordered select-sm"
                  value={addForm.environment}
                  onChange={e => setFormField('environment', e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {ENVIRONMENTS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>

              <label className="form-control">
                <div className="label"><span className="label-text">Location</span></div>
                <select
                  className="select select-bordered select-sm"
                  value={addForm.location}
                  onChange={e => setFormField('location', e.target.value)}
                >
                  {LOCATION_OPTIONS.map(v => <option key={v} value={v}>{locationLabel(v)}</option>)}
                </select>
              </label>
            </div>

            <label className="form-control">
              <div className="label"><span className="label-text">Notes</span></div>
              <textarea
                className="textarea textarea-bordered textarea-sm"
                rows={2}
                value={addForm.notes}
                onChange={e => setFormField('notes', e.target.value)}
              />
            </label>

            <label className="flex items-center gap-2 cursor-pointer mt-1">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={addForm.is_virtual}
                onChange={e => setFormField('is_virtual', e.target.checked)}
              />
              <span className="label-text">Virtual device (not physical hardware)</span>
            </label>

            <div className="modal-action mt-2">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={closeAddModal}
                disabled={adding}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={adding} aria-busy={adding}>
                {adding ? <Spinner size="xs" /> : 'Add device'}
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button disabled={adding} aria-label="Close">close</button>
        </form>
      </dialog>
    </div>
  )
}
